import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import useVisibleActivityTemplates from "utils/useVisibleActivityTemplates";
import classnames from "classnames";
import { connect } from "react-redux";
import { axiosAPI, formatLocalesToOptions } from "utils";
import _ from "lodash";

import DraggableModal from "../DraggableModal/DraggableModal";
import { StringAssignField } from "components/form-components";

import classes from "./AddActivityModal.module.scss";
import { getAvailableActivityTemplateTypes, filterTemplatesByType } from "utils/activityTemplateTypes";
import { imagesURL } from "config";

const AddActivityModal = ({
  show,
  onClose,
  locales,
  activityTemplates,
  onCreate,
  translate
}) => {
  const visibleTemplates = useVisibleActivityTemplates(activityTemplates, show);
  const [templateType, setTemplateType] = useState('Games');
  const [configuredTypes, setConfiguredTypes] = useState([]);
  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    axiosAPI.get('/api/flink-make/common/activity-types')
      .then(({ data }) => {
        if (active && Array.isArray(data.types)) setConfiguredTypes(data.types);
      })
      .catch(() => { /* Keep legacy categories available if the catalog is unavailable. */ });
    return () => { active = false; };
  }, [show]);
  const typedTemplates = filterTemplatesByType(visibleTemplates, templateType);
  const [selectedTemplate, setSelectedTemplate] = useState();
  useEffect(() => {
    if (show) {
      setTemplateType('Games');
      setSelectedTemplate(undefined);
    }
  }, [show]);
  const selectedTemplateVisible = selectedTemplate && typedTemplates.some(t => t._id === selectedTemplate._id);
  useEffect(() => {
    if (!selectedTemplateVisible) setSelectedTemplate(undefined);
  }, [selectedTemplateVisible]);
  const [activityName, setActivityName] = useState("");
  const [activityNameString, setActivityNameString] = useState("");
  const [oldName, setOldName] = useState(activityName || '');

  const localesOptions = useMemo(() => {
    return formatLocalesToOptions(locales);
  }, [locales]);

  /*const [activityLocale, setActivityLocale] = useState(() => {
    const [first] = localesOptions;
    return first;
  });*/

  const [activityLocale, setActivityLocale] = useState(() => {
    const preferred =
      localesOptions?.find(o => o.value === 'en') ||
      localesOptions?.find(o => /english/i.test(o.label));
    const chosen = preferred || localesOptions?.[0];
    // minimal runtime logging
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[AddActivityModal] init activityLocale', { chosen });
    }
    return chosen;
  });

  const addNameByString = ({ text }) => {
    if (activityName){
      setOldName(activityName);
      if (text){
        const name = activityName.split("~")[0]
        setActivityName(`${name}~${translate(text, '', true, false)}`)
        setActivityNameString(`${name}~#${text}`)
      }
      else{
        setActivityName(oldName)
        setActivityNameString(null)
      }
    }
    else{
      setActivityName(text ? translate(text, '', true, false) : oldName)
      setActivityNameString(text || null)
    }         
  }

  return (
    <DraggableModal
      show={show}
      onClose={onClose}
      title={translate(470, "New Activity")}
      withOverlay
    >
      <div className={classes["container"]}>
        <div className={classes.leftColumn}>

          <StringAssignField
            labelClassName={classes["template-descr"]}
            label={translate(1312, "Activity Name (string)")}
            value={!!activityNameString 
            && (parseInt(activityNameString) || parseInt(activityNameString.split("~#")[1]))}
            changeAllHandler={addNameByString}
            withoutAudio
            isTooltip
          />

          <div className={classes.spacer} />

          <label htmlFor="activityName" className={classes["label"]}>
            {translate(1312, "Activity Name:")}
          </label>
          <input
            autoFocus
            type="text"
            className={classes["input"]}
            disabled={!!activityNameString}
            value={activityName}
            onChange={e => setActivityName(_.trimStart(e.target.value))}
          />

          {selectedTemplate && (
            <div className={classes["template-descr"]}>
              <img
                src={`${imagesURL}/TemplateThumbnails/${selectedTemplate.thumbFilename}`}
                alt=""
              />
              {_.trim(selectedTemplate.explanationString) && (
                <p>{translate(selectedTemplate.explanationString)}</p>
              )}
            </div>
          )}
        </div>
        <div className={classes.rightColumn}>
          <fieldset className={classes.templateTypes}>
            <legend>Activity Template Types</legend>
            <div className={classes.typeOptions}>
              {getAvailableActivityTemplateTypes(visibleTemplates, configuredTypes).map(type => (
                <label key={type}>
                  <input
                    type="radio"
                    name="activityTemplateType"
                    value={type}
                    checked={templateType === type}
                    onChange={() => {
                      setTemplateType(type);
                      setSelectedTemplate(undefined);
                    }}
                  />
                  {type}
                </label>
              ))}
            </div>
          </fieldset>
          <div className={classes["template-btns-wrapper"]}>
            {_.chain(typedTemplates)
              .filter(
                templ =>
                  !(templ.availableForWordlists && templ.onlyForWordlists)
              )
              .sortBy(templ => templ.activityTemplateName)
              .map(templ => (
                <button
                  className={classnames(classes["template-btn"], {
                    [classes["active"]]:
                      !!selectedTemplate && selectedTemplate._id === templ._id
                  })}
                  key={templ._id}
                  onClick={() => setSelectedTemplate(templ)}
                >
                  {translate(templ.nameString, templ.activityTemplateName)}
                </button>
              ))
              .value()}
          </div>

          <div className={classes.row}>
            <select 
              className={classes.select} 
              value={activityLocale?.value} 
              onChange={(event) => setActivityLocale(() => {
                const value = event.target.value;
                return localesOptions.find(o => o.value === value);
              })}
            >
              {localesOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              className={classes["save-btn"]}
              disabled={!activityName || !selectedTemplateVisible || !activityLocale}
              onClick={() =>
                selectedTemplateVisible && onCreate({ activityName, activityNameString, activityLocale, template: selectedTemplate })
              }
            >
              {translate(298, "Save")}
            </button>
          </div>
        </div>
      </div>
    </DraggableModal>
  );
};

AddActivityModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  activityTemplates: PropTypes.array.isRequired
};

const mapStateToProps = ({ status: { translate }, common: { locales } }) => ({ translate, locales });

export default connect(mapStateToProps)(AddActivityModal);
